import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import VisitsChart from "@/components/VisitsChart";
import MonthlyRevenueChart from "@/components/MonthlyRevenueChart";
import { STATUS_LABEL, CORRIDORS, formatXOF, formatAmount, formatDate, depositMethodsFor, receptionMethodsFor, DEPOSIT_METHODS, RECEPTION_METHODS } from "@/lib/constants";
import { TrendingUp, Clock, CheckCircle2, Wallet, MessageCircle, Users, Search, ShieldAlert, Banknote, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const FILTERS = [
  { k: "", label: "Toutes" },
  { k: "en_attente", label: STATUS_LABEL.en_attente },
  { k: "verifie", label: STATUS_LABEL.verifie },
  { k: "envoye", label: STATUS_LABEL.envoye },
  { k: "termine", label: STATUS_LABEL.termine },
  { k: "annule", label: STATUS_LABEL.annule },
];

const CORRIDOR_FILTERS = [
  { k: "", label: "Tous corridors" },
  { k: "tg_ga", label: CORRIDORS.tg_ga.short },
  { k: "ga_fr", label: CORRIDORS.ga_fr.short },
  { k: "ga_rw", label: CORRIDORS.ga_rw?.short || "GA → RW" },
  { k: "fr_ga", label: CORRIDORS.fr_ga?.short || "FR → GA" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [today, setToday] = useState(null);
  const [usersTotal, setUsersTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [corridorFilter, setCorridorFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const qs = [];
        if (filter) qs.push(`status=${filter}`);
        if (corridorFilter) qs.push(`corridor=${corridorFilter}`);
        if (showArchived) qs.push("archived=true");
        const [statsRes, listRes, usersRes, todayRes] = await Promise.all([
          api.get("/admin/stats"),
          api.get(`/admin/transactions${qs.length ? "?" + qs.join("&") : ""}`),
          api.get("/admin/users?limit=1").catch(() => ({ data: { total: 0 } })),
          api.get("/admin/stats/today").catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setStats(statsRes.data);
          setItems(listRes.data);
          setUsersTotal(usersRes.data?.total || 0);
          setToday(todayRes.data);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filter, corridorFilter, showArchived, refreshTick]);

  const cards = [
    { label: "Transactions", value: stats?.total ?? 0, icon: TrendingUp, accent: "text-[var(--sl-primary)]" },
    { label: "Volume total", value: formatXOF(stats?.total_volume ?? 0), icon: Wallet, accent: "text-[var(--sl-secondary-dark)]" },
    { label: "Utilisateurs inscrits", value: usersTotal, icon: Users, accent: "text-blue-600", link: "/admin/utilisateurs" },
    { label: "En attente", value: stats?.by_status?.en_attente?.count ?? 0, icon: Clock, accent: "text-amber-600" },
    { label: "Terminées", value: stats?.by_status?.termine?.count ?? 0, icon: CheckCircle2, accent: "text-emerald-600" },
  ];

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold">Tableau de bord</h1>
      <p className="text-gray-600 mt-1">Vue d&apos;ensemble des transferts Swiftlink-Africa.</p>

      {/* Today's snapshot */}
      {today && (
        <div className="mt-5 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 sm:p-5 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="dashboard-today">
          <div className="col-span-2 sm:col-span-3 xl:col-span-5">
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Activité du jour ({today.date})</div>
          </div>
          <TodayCard label="Transactions" value={today.tx_total} />
          <TodayCard label="En attente" value={today.tx_pending} accent="text-amber-600" />
          <TodayCard label="Terminées" value={today.tx_done} accent="text-emerald-700" />
          <TodayCard label="Volume" value={formatXOF(today.volume_xaf || 0)} accent="text-[var(--sl-primary)]" />
          <TodayCard label="Frais perçus" value={formatXOF(today.fees_xaf || 0)} accent="text-[var(--sl-secondary-dark)]" />
        </div>
      )}

      {/* Search bar + quick links */}
      <div className="mt-5 flex flex-wrap gap-2 items-center">
        <div className="flex-1 basis-full sm:basis-auto min-w-0 sm:min-w-[220px] sl-card p-0 flex items-center gap-2 px-3">
          <Search size={16} className="text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (code, expéditeur, bénéficiaire, téléphone…)"
            className="flex-1 outline-none text-sm bg-transparent py-3"
            data-testid="admin-search"
          />
          {search && (
            <button type="button" onClick={() => setSearch("")} className="text-xs text-gray-400 hover:text-gray-700">×</button>
          )}
        </div>
        <Link to="/admin/fraude" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-red-50 text-red-700 text-xs font-semibold border border-red-200 hover:bg-red-100" data-testid="link-fraud-page">
          <ShieldAlert size={13} /> Comptes suspects
        </Link>
        <button
          type="button"
          onClick={() => setShowManual(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[var(--sl-primary)] text-white text-xs font-semibold hover:opacity-90"
          data-testid="manual-tx-open"
        >
          <Plus size={14} /> Nouvelle transaction
        </button>
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border ${showArchived ? "bg-gray-700 text-white border-gray-700" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"}`}
          data-testid="toggle-archived"
        >
          {showArchived ? "📥 Archives (visible)" : "📥 Archives"}
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
        {cards.map((c, i) => {
          const inner = (
            <>
              <div className="flex items-center justify-between">
                <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-500">{c.label}</div>
                <c.icon size={18} className={c.accent} />
              </div>
              <div className="mt-2 sm:mt-3 font-heading text-lg sm:text-2xl font-bold truncate">{c.value}</div>
            </>
          );
          return c.link ? (
            <Link key={i} to={c.link} className="sl-card sl-card-hoverable" data-testid={`stat-card-${i}`}>{inner}</Link>
          ) : (
            <div key={i} className="sl-card" data-testid={`stat-card-${i}`}>{inner}</div>
          );
        })}
      </div>

      <div className="mt-6">
        <VisitsChart />
      </div>

      <div className="mt-6">
        <MonthlyRevenueChart />
      </div>

      <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`px-4 py-2 rounded-full text-sm border transition ${
                filter === f.k
                  ? "bg-[var(--sl-primary)] text-white border-[var(--sl-primary)]"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`filter-${f.k || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          {CORRIDOR_FILTERS.map((f) => (
            <button
              key={f.k}
              onClick={() => setCorridorFilter(f.k)}
              className={`px-4 py-2 rounded-full text-sm border transition ${
                corridorFilter === f.k
                  ? "bg-[var(--sl-secondary)] text-gray-900 border-[var(--sl-secondary)]"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`corridor-filter-${f.k || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="sl-card mt-6 p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Chargement…</div>
        ) : (() => {
          const q = (search || "").trim().toLowerCase();
          const filtered = q
            ? items.filter((t) => [t.code, t.sender_name, t.sender_phone, t.sender_email, t.beneficiary_name, t.beneficiary_phone, t.beneficiary_email, t.beneficiary_account]
                .some((v) => (v || "").toLowerCase().includes(q)))
            : items;
          if (filtered.length === 0) return <div className="p-10 text-center text-gray-500">Aucune transaction.</div>;
          return (
            <>
              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-100" data-testid="admin-tx-mobile">
                {filtered.map((t) => (
                  <Link key={t.id} to={`/admin/transactions/${t.id}`} className="block p-3 hover:bg-gray-50" data-testid={`m-tx-${t.code}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-semibold text-[var(--sl-primary)] text-sm">{t.code}</span>
                      <StatusBadge status={t.status} cancellationReason={t.cancellation_reason} />
                    </div>
                    <div className="mt-1.5 text-sm font-medium truncate">{t.sender_name} → {t.beneficiary_name}</div>
                    <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                      <span>{CORRIDORS[t.corridor || "tg_ga"]?.short} · {formatDate(t.created_at)}</span>
                      <span className="font-semibold text-gray-900">{formatXOF(t.amount_xof || 0)}</span>
                    </div>
                  </Link>
                ))}
              </div>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-tx-table">
            <thead className="bg-[var(--sl-bg-alt)] text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left p-4">Référence</th>
                <th className="text-left p-4">Corridor</th>
                <th className="text-left p-4">Expéditeur</th>
                <th className="text-left p-4">Bénéficiaire</th>
                <th className="text-right p-4">Montant</th>
                <th className="text-left p-4">Statut</th>
                <th className="text-left p-4">Créée</th>
                <th className="p-4" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const waNumber = (t.sender_phone || "").replace(/\D/g, "");
                const waMessage = encodeURIComponent(
                  `Bonjour ${t.sender_name},\n\nNous avons bien reçu votre demande Swiftlink-Africa ${t.code}.\nNous vérifions le dépôt et reviendrons vers vous très vite. Merci de votre confiance !`
                );
                return (
                <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="p-4 font-mono font-semibold text-[var(--sl-primary)]">{t.code}</td>
                  <td className="p-4 text-xs whitespace-nowrap">
                    <span className="font-mono">{CORRIDORS[t.corridor || "tg_ga"]?.short}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span>{t.sender_name}</span>
                      {waNumber && (
                        <a
                          href={`https://wa.me/${waNumber}?text=${waMessage}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title={`Écrire à ${t.sender_name} sur WhatsApp`}
                          className="w-6 h-6 rounded-full grid place-items-center hover:scale-110 transition"
                          style={{ background: "#25D366" }}
                          data-testid={`wa-client-${t.code}`}
                        >
                          <MessageCircle size={12} className="text-white" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="p-4">{t.beneficiary_name}</td>
                  <td className="p-4 text-right">{formatAmount(t.amount_xof, t.source_currency || "XOF")}</td>
                  <td className="p-4"><StatusBadge status={t.status} cancellationReason={t.cancellation_reason} /></td>
                  <td className="p-4 text-gray-500">{formatDate(t.created_at)}</td>
                  <td className="p-4 text-right">
                    <Link to={`/admin/transactions/${t.id}`} className="text-[var(--sl-primary)] font-medium text-sm hover:underline" data-testid={`tx-view-${t.code}`}>
                      Voir →
                    </Link>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          </>
          );
        })()}
      </div>

      {showManual && (
        <ManualTxModal
          onClose={() => setShowManual(false)}
          onCreated={() => { setShowManual(false); setRefreshTick((t) => t + 1); }}
        />
      )}
    </div>
  );
}

const MANUAL_STATUS = [
  { k: "en_attente", label: "En attente (à vérifier)" },
  { k: "verifie", label: "Vérifié (dépôt confirmé)" },
  { k: "envoye", label: "Envoyé (fonds transmis)" },
  { k: "termine", label: "Terminé" },
];

function ManualTxModal({ onClose, onCreated }) {
  const [corridor, setCorridor] = useState("tg_ga");
  const [form, setForm] = useState({
    sender_name: "", sender_phone: "", sender_email: "",
    beneficiary_name: "", beneficiary_phone: "", beneficiary_email: "", beneficiary_account: "",
    amount_xof: "", deposit_method: "", reception_method: "", status: "verifie", note: "",
  });
  const [saving, setSaving] = useState(false);

  const c = CORRIDORS[corridor];
  const depOptions = depositMethodsFor(corridor);
  const recOptions = receptionMethodsFor(corridor);
  const recMeta = RECEPTION_METHODS[form.reception_method];
  const needsAccount = recMeta?.fields?.includes("account");
  const needsEmail = recMeta?.fields?.includes("email");
  const needsPhone = recMeta?.fields?.includes("phone");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCorridorChange = (val) => {
    setCorridor(val);
    setForm((f) => ({ ...f, deposit_method: "", reception_method: "", beneficiary_account: "", beneficiary_email: "", beneficiary_phone: "" }));
  };

  const submit = async () => {
    if (!form.sender_name.trim() || !form.beneficiary_name.trim()) { toast.error("Nom expéditeur et bénéficiaire requis"); return; }
    if (!form.sender_phone.trim()) { toast.error("Téléphone de l'expéditeur requis"); return; }
    if (!form.deposit_method) { toast.error("Choisissez la méthode de dépôt"); return; }
    if (!form.reception_method) { toast.error("Choisissez la méthode de réception"); return; }
    const amt = parseFloat(form.amount_xof);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Montant invalide"); return; }
    setSaving(true);
    try {
      const payload = {
        corridor,
        sender_name: form.sender_name.trim(),
        sender_phone: form.sender_phone.trim(),
        sender_email: form.sender_email.trim() || undefined,
        beneficiary_name: form.beneficiary_name.trim(),
        beneficiary_phone: form.beneficiary_phone.trim() || undefined,
        beneficiary_email: form.beneficiary_email.trim() || undefined,
        beneficiary_account: form.beneficiary_account.trim() || undefined,
        amount_xof: amt,
        deposit_method: form.deposit_method,
        reception_method: form.reception_method,
        status: form.status,
        note: form.note.trim() || undefined,
      };
      const { data } = await api.post("/admin/transactions/manual", payload);
      toast.success(`Transaction ${data.code} créée`);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} role="dialog" aria-modal="true" data-testid="manual-tx-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-lg w-full p-5 sm:p-6 max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-lg font-bold">Nouvelle transaction manuelle</h3>
            <p className="text-xs text-gray-500">Pour un client passé directement via WhatsApp.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1" data-testid="manual-tx-close"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="sl-label">Corridor</label>
            <select className="sl-input" value={corridor} onChange={(e) => onCorridorChange(e.target.value)} data-testid="manual-corridor">
              {Object.values(CORRIDORS).map((co) => (
                <option key={co.id} value={co.id}>{co.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="sl-label">Nom expéditeur</label>
              <input className="sl-input" value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} data-testid="manual-sender-name" />
            </div>
            <div>
              <label className="sl-label">Tél. expéditeur</label>
              <input className="sl-input" value={form.sender_phone} onChange={(e) => set("sender_phone", e.target.value)} placeholder={c?.senderDialCode} data-testid="manual-sender-phone" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="sl-label">Nom bénéficiaire</label>
              <input className="sl-input" value={form.beneficiary_name} onChange={(e) => set("beneficiary_name", e.target.value)} data-testid="manual-beneficiary-name" />
            </div>
            <div>
              <label className="sl-label">Méthode réception</label>
              <select className="sl-input" value={form.reception_method} onChange={(e) => set("reception_method", e.target.value)} data-testid="manual-reception">
                <option value="">— choisir —</option>
                {recOptions.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>

          {needsPhone && (
            <div>
              <label className="sl-label">Tél. bénéficiaire</label>
              <input className="sl-input" value={form.beneficiary_phone} onChange={(e) => set("beneficiary_phone", e.target.value)} placeholder={c?.beneficiaryDialCode} data-testid="manual-beneficiary-phone" />
            </div>
          )}
          {needsEmail && (
            <div>
              <label className="sl-label">Email PayPal bénéficiaire</label>
              <input className="sl-input" value={form.beneficiary_email} onChange={(e) => set("beneficiary_email", e.target.value)} data-testid="manual-beneficiary-email" />
            </div>
          )}
          {needsAccount && (
            <div>
              <label className="sl-label">{recMeta?.hint || "Compte de réception"}</label>
              <input className="sl-input" value={form.beneficiary_account} onChange={(e) => set("beneficiary_account", e.target.value)} data-testid="manual-beneficiary-account" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="sl-label">Méthode de dépôt</label>
              <select className="sl-input" value={form.deposit_method} onChange={(e) => set("deposit_method", e.target.value)} data-testid="manual-deposit">
                <option value="">— choisir —</option>
                {depOptions.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="sl-label">Montant ({c?.source_currency})</label>
              <input type="number" className="sl-input" value={form.amount_xof} onChange={(e) => set("amount_xof", e.target.value)} data-testid="manual-amount" />
            </div>
          </div>

          <div>
            <label className="sl-label">Statut initial</label>
            <select className="sl-input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="manual-status">
              {MANUAL_STATUS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
            </select>
          </div>

          <div>
            <label className="sl-label">Note interne (optionnel)</label>
            <textarea className="sl-input" rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Ex. Reçu via WhatsApp, capture envoyée par le client" data-testid="manual-note" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="sl-btn-outline text-sm" data-testid="manual-cancel">Annuler</button>
          <button type="button" onClick={submit} disabled={saving} className="sl-btn-primary text-sm disabled:opacity-60" data-testid="manual-submit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Créer la transaction
          </button>
        </div>
      </div>
    </div>
  );
}

function TodayCard({ label, value, accent }) {
  return (
    <div className="rounded-lg bg-white border border-gray-200 px-3 py-2 min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold truncate">{label}</div>
      <div className={`font-heading text-base sm:text-lg font-bold mt-0.5 truncate ${accent || "text-gray-900"}`}>{value}</div>
    </div>
  );
}
